import { OwnershipSplitType, OwnerType } from '@app/shared/models/contracts/enums/shared-enums';
import { EquityItem } from '../../../CapitalStructureItemFormERF2';
import { TableSection } from '../types';

/**
 * Gets the ProRata amount value based on table section
 */
const getProRataAmountValue = (
  equityItem: EquityItem,
  tableSection: TableSection
): number | null => {
  return tableSection === TableSection.AmountAdjustment
    ? equityItem.totalValueDelta
    : tableSection === TableSection.RealBuybackValue
    ? equityItem.totalRealBuybackMovements
    : equityItem.totalValueAfterMovements;
};

/**
 * Gets the ProRata shares value based on table section
 */
const getProRataSharesValue = (
  equityItem: EquityItem,
  tableSection: TableSection
): number | null => {
  return tableSection === TableSection.SharesAdjustment
    ? equityItem.totalSharesDelta
    : equityItem.totalSharesAfterMovements;
};

/**
 * Gets the owner amount value based on table section
 */
const getOwnerAmountValue = (
  equityItem: EquityItem,
  tableSection: TableSection,
  section: keyof typeof OwnerType
): number | null => {
  return tableSection === TableSection.AmountAdjustment
    ? equityItem.valueDeltas[section]
    : tableSection === TableSection.RealBuybackValue
    ? equityItem.realBuybackValueDelta?.[section] ?? null
    : equityItem.ownershipAfterMovements[section]?.amount;
};

/**
 * Gets the owner shares value based on table section
 */
const getOwnerSharesValue = (
  equityItem: EquityItem,
  tableSection: TableSection,
  section: keyof typeof OwnerType
): number | null => {
  return tableSection === TableSection.SharesAdjustment
    ? equityItem.shareDeltas[section]
    : equityItem.ownershipAfterMovements[section]?.numberOfShares;
};

/**
 * Gets the value for ProRata totals based on table section and field type
 */
const getProRataValue = (
  equityItem: EquityItem,
  tableSection: TableSection,
  isAmountField: boolean
): number | null => {
  return isAmountField
    ? getProRataAmountValue(equityItem, tableSection)
    : getProRataSharesValue(equityItem, tableSection);
};

/**
 * Gets the value for individual owner based on table section and field type
 */
const getOwnerValue = (
  equityItem: EquityItem,
  tableSection: TableSection,
  section: keyof typeof OwnerType,
  isAmountField: boolean
): number | null => {
  return isAmountField
    ? getOwnerAmountValue(equityItem, tableSection, section)
    : getOwnerSharesValue(equityItem, tableSection, section);
};

/**
 * Calculates the readonly value for a table cell based on equity item and section
 */
export const getReadOnlyValue = (
  equityItem: EquityItem,
  tableSection: TableSection,
  section: keyof typeof OwnerType | OwnershipSplitType.ProRata,
  isAmountField: boolean
): number | null => {
  if (section === OwnershipSplitType.ProRata) {
    return getProRataValue(equityItem, tableSection, isAmountField);
  } else {
    return getOwnerValue(equityItem, tableSection, section, isAmountField);
  }
};
